import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControl, FormControlLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, Switch, TextField, Typography
} from '@mui/material';
import { RECIPIENT_TYPES } from '../../services/accounting/commission.service';
import { EMPLOYMENT_TYPES } from '../../services/accounting/staff.service';
import { isLoginEnabled } from '../../pages/team/teamMembers';
import { validateMember, memberPayload, staffChanges, initialForm, withType, withOwnerChoice, salaryAllowed, ADMIN_VALUE } from './memberForm';

const f = { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' };


/**
 * UserDialog — create or edit a typing-center team member.
 * The role picker offers "Admin" (full access) plus every active custom role; picking a
 * custom role saves the user as TC_STAFF with that role_id.
 *
 * "Earns commission" (finance write only) saves the member's commission profile: onSave gets it as a second
 * argument, null when the switch is off and the member had no profile.
 *
 * "Can sign in" off saves a person without a login (no username, password or role; no seat). Employment type and
 * salary (finance write only) go to the staff profile: onSave gets them as a third argument { profile, salary }.
 *
 * Props: open, onClose, onSave(payload, commission, staff) -> Promise, user (null = create), roles, saving,
 *        recipient (the member's commission profile or null), canCommission,
 *        bank (the person's bank details record or null), grantAccess (open a member without sign-in with sign-in on, Role Admin),
 *        profile (the member's staff profile or null), canEditSalary,
 *        ownerOpts ({ partners: [{id,label}], currentPartnerId, canSetUp }), businessType ('SOLE_ESTABLISHMENT' | 'COMPANY')
 * An Owner is linked to a partner row from Owner Drawings → Partners (or sets up the 100% owner); onSave gets
 * { choice, currentPartnerId } as a fourth argument.
 */
const EMPTY_COMMISSION = { earns: false, type: 'EXTERNAL_AGENT', trnNumber: '', active: true };
const recipientTypeFor = (employmentType) => (['EMPLOYEE', 'OWNER'].includes(employmentType) ? 'INTERNAL_STAFF' : 'EXTERNAL_AGENT');

const thisMonth = () => new Date().toISOString().slice(0, 7);

const UserDialog = ({
  open, onClose, onSave, user, roles = [], saving = false, recipient = null, canCommission = false,
  profile = null, bank = null, canEditSalary = false, grantAccess = false,
  ownerOpts = { partners: [], currentPartnerId: null, canSetUp: false }, businessType = 'SOLE_ESTABLISHMENT',
}) => {
  const isEdit = Boolean(user);
  const hadLogin = isEdit && isLoginEnabled(user);
  const [form, setForm] = useState(() => initialForm(null, null, thisMonth()));
  const showSalaryParts = form.salaryMode === 'parts';
  const wasOpen = useRef(false);
  const [commission, setCommission] = useState(EMPTY_COMMISSION);
  const [error, setError] = useState(null);

  // Reset only when the dialog opens: after a partial save the parent swaps in the saved member, and what the
  // person typed must stay for the retry.
  useEffect(() => {
    const opening = open && !wasOpen.current;
    wasOpen.current = open;
    if (!opening) return;
    const start = initialForm(user, profile, thisMonth(), ownerOpts.currentPartnerId, bank);
    setForm(grantAccess && user && !isLoginEnabled(user) ? { ...start, loginEnabled: true, roleValue: start.roleValue || ADMIN_VALUE } : start);
    setCommission(recipient ? {
      earns: true, type: recipient.type, trnNumber: recipient.trnNumber || '', active: recipient.active !== false,
    } : EMPTY_COMMISSION);
    setError(null);
  }, [open, user, profile, recipient, bank, ownerOpts.currentPartnerId]);

  const setC = (k) => (e) => setCommission((c) => ({ ...c, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  // A new commission profile starts with the type that matches the person: staff for employees and owners.
  const toggleEarns = (e) => {
    const earns = e.target.checked;
    setCommission((c) => ({ ...c, earns, type: earns && !recipient ? recipientTypeFor(form.employmentType) : c.type }));
  };

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: k === 'username' ? e.target.value.toLowerCase() : e.target.value }));
  const activeRoles = roles.filter((r) => r.status !== 'ARCHIVED');

  const handleSave = async () => {
    const ctx = { isEdit, hadLogin };
    const invalid = validateMember(form, ctx);
    if (invalid) { setError(invalid); return; }
    setError(null);
    const payload = memberPayload(form, ctx);
    const staff = staffChanges(canEditSalary ? form : { ...form, salary: '', basic: '', allowances: '' }, profile?.currentSalary, businessType, bank);
    const owner = form.employmentType === 'OWNER' ? { choice: form.ownerChoice, currentPartnerId: ownerOpts.currentPartnerId } : null;
    const commissionPayload = canCommission && (commission.earns || recipient) ? {
      type: commission.type, trnNumber: commission.trnNumber.trim(),
      active: commission.earns ? commission.active : false,
    } : null;
    try {
      await onSave(payload, commissionPayload, staff, owner);
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Could not save the member.');
    }
  };

  const fieldProps = { size: 'small', fullWidth: true, InputProps: { sx: { fontSize: f.sm } }, InputLabelProps: { sx: { fontSize: f.sm } } };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: f.md }}>{isEdit ? 'Edit Team Member' : 'Add Team Member'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2, fontSize: f.xs }} onClose={() => setError(null)}>{error}</Alert>}
        <Typography sx={{ fontSize: f.xs, color: 'text.secondary', mb: 0.5 }}>Type</Typography>
        <RadioGroup row value={form.employmentType} onChange={(e) => setForm((p) => withType(p, e.target.value))} sx={{ mb: 1 }}>
          {Object.entries(EMPLOYMENT_TYPES).map(([v, l]) => (
            <FormControlLabel key={v} value={v} control={<Radio size="small" />}
              label={<Typography sx={{ fontSize: f.sm }}>{l}</Typography>} />
          ))}
        </RadioGroup>

        {form.employmentType === 'OWNER' && (
          <Box sx={{ mb: 1.5 }}>
            {ownerOpts.partners.length > 0 || ownerOpts.canSetUp ? (
              <TextField select label="Owner of" value={form.ownerChoice} {...fieldProps}
                onChange={(e) => setForm((p) => withOwnerChoice(p, e.target.value, ownerOpts.partners))}
                helperText="Ownership % is managed in Owner Drawings → Partners">
                {ownerOpts.partners.map((p) => <MenuItem key={p.id} value={p.id} sx={{ fontSize: f.sm }}>{p.label}</MenuItem>)}
                {ownerOpts.canSetUp && (
                  <MenuItem value="SETUP" sx={{ fontSize: f.sm }}>Set up owner (100%) — this person owns the business</MenuItem>
                )}
              </TextField>
            ) : (
              <Alert severity="info" sx={{ fontSize: f.xs }}>
                Every partner is already linked to a person. Add a partner in Owner Drawings → Partners first.
              </Alert>
            )}
            {form.ownerChoice === 'SETUP' && (
              <TextField label="Owner's full name" value={form.fullName} onChange={set('fullName')} required sx={{ mt: 1.5 }} {...fieldProps} />
            )}
            {businessType === 'COMPANY' ? (
              <FormControlLabel sx={{ mt: 1 }}
                control={<Switch size="small" checked={form.alsoSalary}
                  onChange={(e) => setForm((p) => ({ ...p, alsoSalary: e.target.checked }))} />}
                label={<Typography sx={{ fontSize: f.sm }}>Also paid a salary</Typography>} />
            ) : (
              <Typography sx={{ fontSize: f.xs, color: 'text.secondary', mt: 1 }}>
                Sole establishment: an owner's pay is recorded as a drawing, not a salary.{' '}
                <Box component="a" href="/accounting/drawings" sx={{ color: 'primary.main' }}>Record drawing</Box>
              </Typography>
            )}
          </Box>
        )}

        {form.employmentType !== 'OWNER' && (
          <TextField label="Full name" value={form.fullName} onChange={set('fullName')} required autoFocus sx={{ mb: 1.5 }} {...fieldProps} />
        )}

        {salaryAllowed(form, businessType) && canEditSalary && (
          <Box sx={{ mb: 1.5 }}>
            <Box display="flex" gap={1}>
              {showSalaryParts ? (
                <>
                  <TextField label="Basic (AED)" type="number" value={form.basic} onChange={set('basic')} {...fieldProps} />
                  <TextField label="Allowances (AED)" type="number" value={form.allowances} onChange={set('allowances')} {...fieldProps} />
                </>
              ) : (
                <TextField label="Monthly salary (AED)" type="number" value={form.salary} onChange={set('salary')} {...fieldProps} />
              )}
              <TextField label="Salary from" type="month" value={form.salaryFrom} onChange={set('salaryFrom')}
                {...fieldProps} InputLabelProps={{ shrink: true, sx: { fontSize: f.sm } }} />
              <TextField label="Join date" type="date" value={form.joinDate} onChange={set('joinDate')}
                {...fieldProps} InputLabelProps={{ shrink: true, sx: { fontSize: f.sm } }} />
            </Box>
            <Button size="small" onClick={() => setForm((p) => ({ ...p, salaryMode: p.salaryMode === 'parts' ? 'single' : 'parts' }))}
              sx={{ fontSize: f.xs, textTransform: 'none', px: 0, mt: 0.5 }}>
              {showSalaryParts ? 'Enter one monthly amount' : 'Split into basic and allowances'}
            </Button>
            {profile?.currentSalary && (
              <Typography sx={{ fontSize: f.xs, color: 'text.secondary' }}>
                Current: AED {Number(profile.currentSalary.total).toLocaleString('en-US', { minimumFractionDigits: 2 })} since{' '}
                {new Date(`${profile.currentSalary.effectiveFrom}T00:00:00`).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
              </Typography>
            )}
            <Typography sx={{ fontSize: f.xs, color: 'text.secondary' }}>
              Pre-fills the monthly salary expense. A change applies from the month chosen; earlier months keep their amount.
            </Typography>
          </Box>
        )}

        {canEditSalary && (
          <Box sx={{ mb: 1.5 }}>
            <Box display="flex" alignItems="center" gap={0.75} mb={0.75}>
              <Typography sx={{ fontSize: f.sm, fontWeight: 600 }}>Bank details</Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              <TextField label="Bank name" value={form.bankName} onChange={set('bankName')} {...fieldProps} sx={{ gridColumn: '1 / -1' }} />
              <TextField label="IBAN" value={form.iban} onChange={set('iban')} placeholder="AE07 0331 2345 6789 0123 456" {...fieldProps} />
              <TextField label="Account number" value={form.accountNumber} onChange={set('accountNumber')} {...fieldProps} />
            </Box>
            <Typography sx={{ fontSize: f.xs, color: 'text.secondary', mt: 0.5 }}>
              Used for salary (WPS) and commission payouts — one place for this person.
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 1 }} />
        <FormControlLabel
          control={<Switch size="small" checked={form.loginEnabled}
            onChange={(e) => setForm((p) => ({ ...p, loginEnabled: e.target.checked }))} />}
          label={<Typography sx={{ fontSize: f.sm, fontWeight: 600 }}>Can sign in to Thaklis Pro</Typography>}
        />
        <Typography sx={{ fontSize: f.xs, color: 'text.secondary', mb: 1 }}>
          {form.loginEnabled
            ? 'Uses one user seat on your plan.'
            : 'No username or password; does not use a seat. Turn this on later to give them access.'}
        </Typography>
        {form.loginEnabled && (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 1.5, rowGap: 1.5, mb: 1 }}>
            <TextField label="Username" value={form.username} onChange={set('username')} required {...fieldProps}
              autoComplete="off" inputProps={{ autoComplete: 'off' }}
              sx={{ gridColumn: !isEdit || !hadLogin ? 'auto' : '1 / -1' }} />
            {(!isEdit || !hadLogin) && (
              <TextField label="Password" type="password" value={form.password} onChange={set('password')} required {...fieldProps}
                autoComplete="new-password" inputProps={{ autoComplete: 'new-password' }} />
            )}
            <TextField label="Email" type="email" value={form.email} onChange={set('email')} required {...fieldProps}
              autoComplete="off" inputProps={{ autoComplete: 'off' }} />
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: f.sm }}>Role</InputLabel>
              <Select label="Role" value={form.roleValue} onChange={set('roleValue')} sx={{ fontSize: f.sm }}>
                <MenuItem value={ADMIN_VALUE} sx={{ fontSize: f.sm }}>
                  Admin
                  <Typography component="span" sx={{ fontSize: f.xs, color: 'text.secondary', ml: 1 }}>full access</Typography>
                </MenuItem>
                {activeRoles.map((r) => (
                  <MenuItem key={r.id} value={`ROLE:${r.id}`} sx={{ fontSize: f.sm }}>{r.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            </Box>
            {activeRoles.length === 0 && (
              <Typography sx={{ fontSize: f.xs, color: 'text.secondary', mb: 1 }}>
                No custom roles yet — create one under Configuration → Roles to give a member limited access.
              </Typography>
            )}
          </>
        )}
        {isEdit && (
          <FormControlLabel
            control={<Switch size="small" checked={form.active} onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))} />}
            label={<Typography sx={{ fontSize: f.sm }}>Active</Typography>}
          />
        )}
        {canCommission && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <FormControlLabel
              control={<Switch size="small" checked={commission.earns} onChange={toggleEarns} />}
              label={<Typography sx={{ fontSize: f.sm, fontWeight: 600 }}>Earns commission</Typography>}
            />
            <Typography sx={{ fontSize: f.xs, color: 'text.secondary', mb: 1 }}>
              Internal tasks assigned to this member earn commission at their rates (Commissions → Rates).
              {recipient && !commission.earns ? ' Turning this off keeps their history and stops new commission.' : ''}
            </Typography>
            {commission.earns && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                <TextField select label="Recipient type" value={commission.type} onChange={setC('type')} {...fieldProps}>
                  {Object.entries(RECIPIENT_TYPES).map(([v, l]) => <MenuItem key={v} value={v} sx={{ fontSize: f.sm }}>{l}</MenuItem>)}
                </TextField>
                <TextField label="TRN (if VAT registered)" value={commission.trnNumber} onChange={setC('trnNumber')} {...fieldProps} />
                <Typography sx={{ fontSize: f.xs, color: 'text.secondary', gridColumn: '1 / -1' }}>
                  Commission is paid to the bank details above.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} sx={{ fontSize: f.sm }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ fontSize: f.sm }}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}>
          {isEdit ? 'Save' : 'Add Member'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserDialog;
